/**
 * mathliveOptimizations.ts
 *
 * Aggressive memory optimizations and patches for MathLive
 * to prevent memory leaks and reduce memory usage.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { MathfieldElement } from 'mathlive';
/**
 * Aggressively dispose of a MathfieldElement instance
 * This goes beyond the default cleanup to ensure all memory is freed
 */
export function disposeMathfield(mf) {
    if (!mf)
        return;
    // DO NOT REMOVE — Guards against the Strict Mode / duplication disposal bug.
    // React 18 Strict Mode simulates unmount→remount in development. The cleanup
    // schedules a deferred batchDisposeMathfields via requestIdleCallback. If the
    // second mount reuses the same DOM element (setMathData skips rebuild because
    // data is unchanged), the deferred dispose fires on the LIVE math-field,
    // clearing its value and blanking the editor.
    // Guard: skip disposal for any math-field still attached to the document.
    if (mf.isConnected)
        return;
    try {
        // 1. Clear the value to release internal state
        mf.value = '';
        // 2. Remove all event listeners by cloning and replacing
        // This is more thorough than removeEventListener
        if (mf.parentNode) {
            var clone = mf.cloneNode(false);
            mf.parentNode.replaceChild(clone, mf);
            // Now work with the isolated old element
        }
        // 3. Skip aggressive shadow DOM cleanup — removing shadow DOM children
        // destroys MathLive's internal elements (ariaLiveText, etc.) which causes
        // crashes in Firefox when focus/blur events fire on partially-cleaned instances.
        // 4. Remove from DOM if still attached
        if (mf.parentNode) {
            mf.parentNode.removeChild(mf);
        }
        // 5. Clear all attributes
        Array.from(mf.attributes).forEach(function (attr) {
            try {
                mf.removeAttribute(attr.name);
            }
            catch (e) {
                // Ignore
            }
        });
        // 6. Null out any custom properties we might have set
        mf.value = null;
        // Note: shadowRoot is read-only and cannot be set to null
    }
    catch (error) {
        console.warn('Error disposing MathfieldElement:', error);
    }
}
/**
 * Batch dispose multiple mathfield elements efficiently
 */
export function batchDisposeMathfields(mathfields) {
    var fields = Array.isArray(mathfields) ? mathfields : Array.from(mathfields);
    // Use requestIdleCallback if available for non-blocking cleanup
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(function () {
            fields.forEach(disposeMathfield);
        }, { timeout: 100 });
    }
    else {
        // Fallback to setTimeout
        setTimeout(function () {
            fields.forEach(disposeMathfield);
        }, 0);
    }
}
/**
 * Static HTML renderer for math content in viewonly mode
 * This avoids creating MathfieldElement instances entirely
 */
export function renderStaticMath(latex, color) {
    var _a;
    if (!latex)
        return '';
    // For simple cases, return plain HTML instead of using MathLive
    // This dramatically reduces memory usage for viewonly cells
    // Strip MathJax wrappers
    var cleanLatex = latex.replace(/^\\\\\(/, '').replace(/\\\\\)$/, '');
    // Handle simple cases with HTML entities
    if (!cleanLatex.includes('\\')) {
        // Plain text or numbers - no LaTeX commands
        return "<span style=\"color: ".concat(color || '#293273', "\">").concat(cleanLatex, "</span>");
    }
    // For complex math, we need to use MathLive but can optimize
    // by rendering to static HTML using convertLatexToMarkup
    try {
        // Use MathLive's static rendering if available
        if ((_a = window.MathfieldElement) === null || _a === void 0 ? void 0 : _a.convertLatexToMarkup) {
            var markup = window.MathfieldElement.convertLatexToMarkup(cleanLatex);
            return "<span style=\"color: ".concat(color || '#293273', "\">").concat(markup, "</span>");
        }
    }
    catch (e) {
        // Fallback to displaying raw LaTeX
        console.warn('Static math rendering failed:', e);
    }
    // Fallback: return the LaTeX as-is for MathEditor to handle
    return '';
}
/**
 * Lightweight MathfieldElement wrapper with automatic cleanup
 */
var ManagedMathfield = /** @class */ (function () {
    function ManagedMathfield(options) {
        if (options === void 0) { options = {}; }
        this.mf = null;
        this.disposed = false;
        this.mf = new MathfieldElement(__assign({ contentPlaceholder: 'Type' }, options));
        // Disable features not needed for viewonly
        this.mf.smartFence = false;
        this.mf.smartMode = false;
        this.mf.setAttribute('math-virtual-keyboard-policy', 'off');
    }
    ManagedMathfield.prototype.getElement = function () {
        return this.mf;
    };
    ManagedMathfield.prototype.dispose = function () {
        if (this.disposed || !this.mf)
            return;
        disposeMathfield(this.mf);
        this.mf = null;
        this.disposed = true;
    };
    ManagedMathfield.prototype.isDisposed = function () {
        return this.disposed;
    };
    return ManagedMathfield;
}());
export { ManagedMathfield };
/**
 * MathfieldElement pool for reusing instances
 * This can dramatically reduce memory allocation/deallocation
 */
var MathfieldPool = /** @class */ (function () {
    function MathfieldPool() {
        this.pool = [];
        this.maxPoolSize = 20; // Limit pool size to prevent memory hoarding
    }
    MathfieldPool.prototype.acquire = function (options) {
        if (options === void 0) { options = {}; }
        // Try to reuse from pool
        if (this.pool.length > 0) {
            var managed = this.pool.pop();
            if (managed.getElement()) {
                // Reset the element
                managed.getElement().value = '';
                return managed;
            }
        }
        // Create new if pool is empty
        return new ManagedMathfield(options);
    };
    MathfieldPool.prototype.release = function (managed) {
        if (this.pool.length < this.maxPoolSize && !managed.isDisposed()) {
            // Clear it before returning to pool
            var el = managed.getElement();
            if (el) {
                el.value = '';
                this.pool.push(managed);
                return;
            }
        }
        // Pool is full or element is disposed, dispose it
        managed.dispose();
    };
    MathfieldPool.prototype.clear = function () {
        this.pool.forEach(function (managed) { return managed.dispose(); });
        this.pool = [];
    };
    MathfieldPool.prototype.getPoolSize = function () {
        return this.pool.length;
    };
    return MathfieldPool;
}());
// Global pool instance
export var mathfieldPool = new MathfieldPool();
/**
 * Ensure ariaLiveText exists on a MathfieldElement's INTERNAL _Mathfield model.
 *
 * MathLive stores ariaLiveText on the _Mathfield instance (accessed via
 * `mf.mathfield`), NOT on the MathfieldElement itself. The onBlur handler
 * accesses `this.ariaLiveText.textContent` where `this` is the _Mathfield.
 *
 * Firefox: ariaLiveText may not be initialized if connectedCallback's DOM
 * query (`querySelector("[role=status]")`) runs before the shadow DOM HTML
 * is fully inserted. Also cleared to `void 0` during disconnectedCallback.
 *
 * Creates a detached fallback `<div>` so the .textContent access succeeds.
 */
export function ensureAriaLiveText(mf) {
    // Target the internal _Mathfield model where ariaLiveText actually lives
    var internal = mf.mathfield || mf._mathfield;
    if (internal && !internal.ariaLiveText) {
        var div = document.createElement('div');
        div.textContent = '';
        internal.ariaLiveText = div;
    }
    // Also set on the element itself as a secondary fallback
    if (!mf.ariaLiveText) {
        var div = document.createElement('div');
        div.textContent = '';
        mf.ariaLiveText = div;
    }
}
/**
 * Pre-inject a [role="status"] element into the shadow DOM so MathLive's
 * __Mathfield constructor finds it via querySelector('[role=status]') and
 * sets ariaLiveText correctly.  Firefox timing: the shadow DOM HTML may not
 * be fully parsed before the constructor queries it.
 *
 * IMPORTANT: MathLive creates _Mathfield with:
 *   element = shadowRoot.querySelector(':host > span')
 * and then does:
 *   this.ariaLiveText = this.element.querySelector('[role=status]')
 * So the status element must be INSIDE the <span> container, not directly
 * on the shadowRoot.
 */
function preInjectAriaStatusElement(mf) {
    try {
        var sr = mf.shadowRoot;
        if (!sr)
            return;
        // Already exists somewhere in the shadow DOM? Skip.
        if (sr.querySelector('[role="status"]'))
            return;
        // MathLive's shadow DOM: <style>...</style><span></span><slot>
        // The <span> is the container that becomes _Mathfield.element
        var container = sr.querySelector('span') || sr;
        var statusDiv = document.createElement('div');
        statusDiv.setAttribute('role', 'status');
        statusDiv.setAttribute('aria-live', 'polite');
        statusDiv.textContent = '';
        // Visually hidden but still accessible
        statusDiv.style.cssText =
            'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);';
        container.appendChild(statusDiv);
    }
    catch (_) {
        // Ignore — shadow DOM may not be open
    }
}
/**
 * Patch the internal _Mathfield prototype's onBlur/onFocus so that:
 * 1. ariaLiveText is created if missing (prevents the crash)
 * 2. Errors are caught and suppressed (prevents crash propagation)
 * 3. On error ONLY, focusBlurInProgress is reset (prevents stuck state)
 *
 * IMPORTANT: Do NOT use `finally` to reset focusBlurInProgress.
 * MathLive's onFocus intentionally keeps focusBlurInProgress=true for 60ms
 * (reset via setTimeout) to prevent re-entrant blur events during the focus
 * transition. A `finally` block would reset it immediately, allowing a blur
 * to fire in that 60ms window — which hides the keypad on the 2nd click.
 *
 * We access _Mathfield through a MathfieldElement instance after
 * connectedCallback has created the internal model.
 */
var _mathfieldPrototypePatched = false;
function patchInternalOnBlur(mf) {
    if (_mathfieldPrototypePatched)
        return;
    try {
        var internal = mf.mathfield || mf._mathfield;
        if (!internal)
            return;
        var proto = Object.getPrototypeOf(internal);
        if (!proto || !proto.onBlur)
            return;
        var originalOnBlur_1 = proto.onBlur;
        proto.onBlur = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            // Guard: create ariaLiveText if missing
            if (!this.ariaLiveText) {
                var div = document.createElement('div');
                div.textContent = '';
                this.ariaLiveText = div;
            }
            try {
                return originalOnBlur_1.apply(this, args);
            }
            catch (e) {
                // onBlur threw — reset focusBlurInProgress so the mathfield
                // doesn't get permanently stuck unable to handle focus/blur
                this.focusBlurInProgress = false;
                console.warn('[MathLive Patch] onBlur error suppressed:', e);
            }
        };
        var originalOnFocus_1 = proto.onFocus;
        if (originalOnFocus_1) {
            proto.onFocus = function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                if (!this.ariaLiveText) {
                    var div = document.createElement('div');
                    div.textContent = '';
                    this.ariaLiveText = div;
                }
                try {
                    return originalOnFocus_1.apply(this, args);
                }
                catch (e) {
                    // onFocus threw (e.g., while calling previousMathfield.onBlur())
                    // Reset state so the mathfield can still receive future events
                    this.blurred = false;
                    this.focusBlurInProgress = false;
                    console.warn('[MathLive Patch] onFocus error suppressed:', e);
                }
            };
        }
        // Patch handleEvent to guard against undefined keyboardDelegate.
        // When a MathfieldElement is disconnected, disconnectedCallback sets
        // keyboardDelegate = void 0, but the _Mathfield instance may still be
        // registered in the virtual keyboard's listener Set. When the keyboard
        // fires "virtual-keyboard-toggle", handleEvent tries to call
        // this.keyboardDelegate.focus() → crash.
        var originalHandleEvent_1 = proto.handleEvent;
        if (originalHandleEvent_1) {
            proto.handleEvent = function (evt) {
                // Skip entirely if keyboardDelegate has been cleared (element disconnected)
                if (!this.keyboardDelegate && evt.type === 'virtual-keyboard-toggle') {
                    return;
                }
                try {
                    return originalHandleEvent_1.call(this, evt);
                }
                catch (e) {
                    console.warn('[MathLive Patch] handleEvent error suppressed:', e);
                }
            };
        }
        _mathfieldPrototypePatched = true;
    }
    catch (_) {
        // Ignore — internal API may change across MathLive versions
    }
}
/**
 * Install a global capture-phase safety net so that any focus/blur event
 * reaching a math-field element will have ariaLiveText ensured first.
 */
var _globalSafetyInstalled = false;
function installGlobalFocusSafetyNet() {
    if (_globalSafetyInstalled || typeof document === 'undefined')
        return;
    _globalSafetyInstalled = true;
    var guard = function (e) {
        var target = e.target;
        if (target && target.tagName === 'MATH-FIELD') {
            ensureAriaLiveText(target);
        }
    };
    // Capture phase fires before MathLive's internal shadow DOM listeners
    document.addEventListener('focusin', guard, { capture: true });
    document.addEventListener('focusout', guard, { capture: true });
    document.addEventListener('pointerdown', guard, { capture: true });
}
/**
 * Patch MathfieldElement to improve memory management
 */
export function patchMathfieldElement() {
    if (typeof window === 'undefined')
        return;
    // Install global focus/blur safety net
    installGlobalFocusSafetyNet();
    // Store original disconnect
    var originalDisconnectedCallback = MathfieldElement.prototype.disconnectedCallback;
    // Override disconnectedCallback to ensure cleanup
    MathfieldElement.prototype.disconnectedCallback = function () {
        // Call original
        if (originalDisconnectedCallback) {
            originalDisconnectedCallback.call(this);
        }
        // Note: do NOT clear this.value here. Setting this.value = '' on disconnect
        // causes math field content to vanish in duplicated text items because
        // setMathData calls editor.innerHTML = '', which disconnects old math fields
        // and triggers this callback — wiping out the value that was just set.
        // Explicit cleanup is handled by disposeMathfield() instead.
    };
    // Patch connectedCallback to ensure ariaLiveText fallback exists.
    // Firefox: MathLive may not initialize ariaLiveText during connectedCallback
    // because querySelector('[role=status]') runs before shadow DOM HTML is ready.
    // The __Mathfield constructor then fires onPointerDown → onFocus → onBlur
    // which crashes accessing ariaLiveText.textContent.
    //
    // Fix: pre-inject a status element into the shadow DOM BEFORE the original
    // connectedCallback runs, AND wrap in try-catch to survive any remaining crash.
    var originalConnectedCallback = MathfieldElement.prototype.connectedCallback;
    MathfieldElement.prototype.connectedCallback = function () {
        // Step 1: Pre-inject [role=status] into shadow DOM so MathLive's
        // querySelector finds it during __Mathfield constructor
        preInjectAriaStatusElement(this);
        // Step 2: Run original connectedCallback in try-catch
        try {
            if (originalConnectedCallback) {
                originalConnectedCallback.call(this);
            }
        }
        catch (e) {
            console.warn('[MathLive Patch] connectedCallback error caught and suppressed:', e);
        }
        // Step 3: Ensure ariaLiveText fallback on the internal _Mathfield model
        ensureAriaLiveText(this);
        // Step 4: One-time patch of _Mathfield prototype's onBlur/onFocus
        patchInternalOnBlur(this);
    };
    // Bootstrap: create a hidden temp MathfieldElement and insert it into the DOM
    // to trigger connectedCallback (which creates the internal _Mathfield model).
    // This lets us patch the _Mathfield prototype BEFORE any real mathfield is
    // created, so the very first user-facing mathfield already has the guarded
    // onBlur/onFocus methods.
    try {
        if (document.body) {
            var tempMf = new MathfieldElement();
            tempMf.style.cssText = 'display:none;position:absolute;pointer-events:none;';
            document.body.appendChild(tempMf);
            // connectedCallback has now fired → patchInternalOnBlur patched the prototype
            document.body.removeChild(tempMf);
        }
    }
    catch (e) {
        console.warn('[MathLive Patch] Bootstrap prototype patching failed:', e);
    }
    console.log('[MathLive Optimization] MathfieldElement patched for better memory management');
}
/**
 * Force garbage collection hint (works in some browsers with flags)
 */
export function suggestGarbageCollection() {
    if ('gc' in window && typeof window.gc === 'function') {
        try {
            window.gc();
            console.log('[MathLive Optimization] Manual GC triggered');
        }
        catch (e) {
            // GC not available
        }
    }
}
/**
 * Debounced GC suggestion
 */
var gcTimeout = null;
export function scheduleSuggestGarbageCollection(delayMs) {
    if (delayMs === void 0) { delayMs = 1000; }
    if (gcTimeout) {
        clearTimeout(gcTimeout);
    }
    gcTimeout = setTimeout(function () {
        suggestGarbageCollection();
        gcTimeout = null;
    }, delayMs);
}
// Apply patches on module load
if (typeof window !== 'undefined') {
    // Wait for MathfieldElement to be defined
    if (customElements.get('math-field')) {
        patchMathfieldElement();
    }
    else {
        customElements.whenDefined('math-field').then(function () {
            patchMathfieldElement();
        });
    }
}
export default {
    disposeMathfield: disposeMathfield,
    batchDisposeMathfields: batchDisposeMathfields,
    renderStaticMath: renderStaticMath,
    ManagedMathfield: ManagedMathfield,
    mathfieldPool: mathfieldPool,
    patchMathfieldElement: patchMathfieldElement,
    ensureAriaLiveText: ensureAriaLiveText,
    suggestGarbageCollection: suggestGarbageCollection,
    scheduleSuggestGarbageCollection: scheduleSuggestGarbageCollection,
};
